/**
 * The mode gate on `/admin/*` and `/me/*`.
 *
 * Every page inside keeps its own permission gate — this one is about which
 * WORKSPACE the viewer may enter, not which page. `access:admin_mode` and
 * `access:teacher_mode` are granted per role in the tenant config, so an
 * Instructor following an admin link is denied here rather than being handed a
 * workspace that would 403 a page at a time.
 *
 * The exception is an admin link to a page teacher mode also has (a workshop):
 * a viewer who can enter teacher mode is sent to that copy instead of refused.
 *
 * Mirrors `RequirePermission`'s loading + unauthorized UX.
 */
import { Navigate, useLocation } from 'react-router-dom';

import { usePermissions } from '../../hooks/usePermissions';
import UnauthorizedPage from '../UnauthorizedPage';

import { MODE_PERMISSION, teacherPathFor, type ViewMode } from './viewMode';

export interface RequireModeProps {
  mode: ViewMode;
  children: React.ReactNode;
}

export default function RequireMode({ mode, children }: RequireModeProps) {
  const { hasPermission, loading } = usePermissions();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-moss-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (hasPermission(MODE_PERMISSION[mode])) return <>{children}</>;

  const teacherPath = mode === 'admin' ? teacherPathFor(location.pathname) : null;
  if (teacherPath && hasPermission(MODE_PERMISSION.teacher)) {
    return <Navigate to={{ pathname: teacherPath, search: location.search }} replace />;
  }
  return <UnauthorizedPage />;
}
